import { supabase } from '../lib/supabase';

/**
 * Fetch EGW scripture index references for a specific book and chapter
 * @param {string} book - Bible book name
 * @param {number} chapter - Chapter number
 * @returns {Promise<Array>} Array of EGW references
 */
export const fetchEGWReferences = async (book, chapter) => {
  try {
    const { data, error } = await supabase?.from('egw_scripture_index')?.select('*')?.eq('book', book)?.eq('chapter', chapter)?.order('verse_start', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching EGW references:', error);
    throw error;
  }
};

/**
 * Fetch EGW book abbreviations information
 * @param {Array<string>} abbreviations - Array of book abbreviations
 * @returns {Promise<Object>} Map of abbreviation to book info
 */
export const fetchEGWBookInfo = async (bookCodes) => {
  try {
    const uniqueCodes = [...new Set(bookCodes?.filter(Boolean))];
    if (uniqueCodes?.length === 0) return {};
    const { data, error } = await supabase?.from('egw_books')?.select('book_code, title, pub_year, book_id')?.in('book_code', uniqueCodes);
    if (error) throw error;
    const bookMap = {};
    data?.forEach(book => {
      if (!bookMap?.[book?.book_code]) {
        bookMap[book?.book_code] = { title: book?.title, pubYear: book?.pub_year, bookId: book?.book_id };
      }
    });
    return bookMap;
  } catch (error) {
    console.error('Error fetching EGW book info:', error);
    throw error;
  }
};

/**
 * Fetch EGW references for a specific verse
 * @param {string} book - Bible book name
 * @param {number} chapter - Chapter number
 * @param {number} verse - Verse number
 * @returns {Promise<Array>} Array of EGW references for the specific verse
 */
export const fetchEGWReferencesForVerse = async (book, chapter, verse) => {
  try {
    const { data, error } = await supabase?.from('egw_scripture_index')?.select('*')?.eq('book', book)?.eq('chapter', chapter)?.lte('verse_start', verse)?.gte('verse_end', verse)?.order('verse_start', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching EGW references for verse:', error);
    throw error;
  }
};

/**
 * Fetch EGW insights for a specific verse from both scripture_index and bible_references
 * @param {string} book - Bible book name
 * @param {number} chapter - Chapter number
 * @param {number} verse - Verse number
 * @returns {Promise<Object>} Object with scriptureIndex and bibleReferences arrays
 */
export const fetchEGWInsightsForVerse = async (book, chapter, verse) => {
  try {
    // Fetch from scripture_index (curated references)
    const { data: scriptureIndexData, error: scriptureError } = await supabase
      ?.from('egw_scripture_index')
      ?.select('*')
      ?.eq('book', book)
      ?.eq('chapter', chapter)
      ?.lte('verse_start', verse)
      ?.gte('verse_end', verse)
      ?.order('verse_start', { ascending: true });

    if (scriptureError) throw scriptureError;

    // Fetch from bible_references (direct quotes)
    const { data: bibleReferencesData, error: referencesError } = await supabase
      ?.from('egw_bible_references')
      ?.select('*')
      ?.eq('bible_book', book)
      ?.eq('bible_chapter', chapter)
      ?.or(`bible_verse_start.eq.${verse},and(bible_verse_start.lte.${verse},bible_verse_end.gte.${verse})`)
      ?.order('bible_verse_start', { ascending: true });

    if (referencesError) throw referencesError;

    // Deduplicate scripture_index by unique combination of egw_book_code + page_start
    const uniqueScriptureIndex = [];
    const seenScriptureKeys = new Set();
    
    scriptureIndexData?.forEach(item => {
      const key = `${item?.egw_book_code}-${item?.page_start}`;
      if (!seenScriptureKeys?.has(key)) {
        seenScriptureKeys?.add(key);
        uniqueScriptureIndex?.push(item);
      }
    });

    // Deduplicate bible_references by unique combination of book_code + page + paragraph
    const uniqueBibleReferences = [];
    const seenReferenceKeys = new Set();
    
    bibleReferencesData?.forEach(item => {
      const key = `${item?.book_code}-${item?.page}-${item?.paragraph}`;
      if (!seenReferenceKeys?.has(key)) {
        seenReferenceKeys?.add(key);
        uniqueBibleReferences?.push(item);
      }
    });

    return {
      scriptureIndex: uniqueScriptureIndex,
      bibleReferences: uniqueBibleReferences
    };
  } catch (error) {
    console.error('Error fetching EGW insights for verse:', error);
    throw error;
  }
};

/**
 * Fetch EGW content paragraphs by content_sequence_id range
 * @param {number} startSequenceId - Starting content_sequence_id
 * @param {number} endSequenceId - Ending content_sequence_id
 * @returns {Promise<Array>} Array of content paragraphs
 */
export const fetchEGWContentBySequenceRange = async (startSequenceId, endSequenceId) => {
  try {
    const { data, error } = await supabase
      ?.from('egw_content')
      ?.select('content_sequence_id, book_code, page_number, paragraph_number, content, toc_title')
      ?.gte('content_sequence_id', startSequenceId)
      ?.lte('content_sequence_id', endSequenceId)
      ?.order('content_sequence_id', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching EGW content by sequence range:', error);
    throw error;
  }
};

/**
 * Fetch single EGW content paragraph by content_sequence_id
 * @param {number} sequenceId - content_sequence_id
 * @returns {Promise<Object>} Content paragraph object
 */
export const fetchEGWContentBySequenceId = async (sequenceId) => {
  try {
    const { data, error } = await supabase
      ?.from('egw_content')
      ?.select('content_sequence_id, book_code, book_id, page_number, paragraph_number, content, toc_title')
      ?.eq('content_sequence_id', sequenceId)
      ?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching EGW content by sequence ID:', error);
    throw error;
  }
};

/**
 * Fetch all paragraphs from a specific page
 * @param {number} bookId - EGW book ID
 * @param {string} pageNumber - Page number
 * @returns {Promise<Array>} Array of all paragraphs on that page
 */
export const fetchEGWFullPageContent = async (bookId, pageNumber) => {
  try {
    const { data, error } = await supabase
      ?.from('egw_content')
      ?.select('content_sequence_id, book_code, page_number, paragraph_number, content, toc_title')
      ?.eq('book_id', bookId)
      ?.eq('page_number', pageNumber)
      ?.order('paragraph_number', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching full page content:', error);
    throw error;
  }
};

/**
 * Fetch paragraph before a given content_sequence_id
 * @param {number} sequenceId - Current content_sequence_id
 * @returns {Promise<Object|null>} Previous paragraph or null
 */
export const fetchPreviousParagraph = async (sequenceId) => {
  try {
    const { data, error } = await supabase
      ?.from('egw_content')
      ?.select('content_sequence_id, book_code, page_number, paragraph_number, content, toc_title')
      ?.lt('content_sequence_id', sequenceId)
      ?.order('content_sequence_id', { ascending: false })
      ?.limit(1)
      ?.maybeSingle();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching previous paragraph:', error);
    throw error;
  }
};

/**
 * Fetch paragraph after a given content_sequence_id
 * @param {number} sequenceId - Current content_sequence_id
 * @returns {Promise<Object|null>} Next paragraph or null
 */
export const fetchNextParagraph = async (sequenceId) => {
  try {
    const { data, error } = await supabase
      ?.from('egw_content')
      ?.select('content_sequence_id, book_code, page_number, paragraph_number, content, toc_title')
      ?.gt('content_sequence_id', sequenceId)
      ?.order('content_sequence_id', { ascending: true })
      ?.limit(1)
      ?.maybeSingle();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching next paragraph:', error);
    throw error;
  }
};

/**
 * Generate EGW Writings link for a reference
 * @param {string} bookLink - EGW book link ID
 * @param {number} pageStart - Starting page number
 * @returns {string} Full URL to EGW Writings
 */
export const generateEGWLink = (bookLink, pageStart) => {
  if (!bookLink) return null;
  const baseUrl = 'https://m.egwwritings.org/en/book';
  return `${baseUrl}/${bookLink}/${pageStart}`;
};

/**
 * Format page reference display
 * @param {Object} ref - Reference object
 * @returns {string} Formatted page reference
 */
export const formatPageReference = (ref) => {
  const pages = ref?.page_end && ref?.page_end !== ref?.page_start
    ? `${ref?.page_start}-${ref?.page_end}`
    : `${ref?.page_start}`;
  
  return `${ref?.egw_book_code} ${pages}`;
};